// @flow

import React, { useState } from 'react';
import { connect } from 'react-redux';
import BoardActivityPinRepReply from 'app/common/react/components/BoardActivities/BoardActivityPinRepReply';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { toggleActivityItemReaction } from 'app/common/redux/actions/activityItem/updateActivityItem';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import {
  type BoardActivityItem,
  type BoardActivityType,
} from 'app/common/redux/reducers/activityItems';
import { Avatar, Box, Icon, Text, Touchable } from 'gestalt';
import { compose, type Dispatch } from 'redux';
import { getNameToShow, getTimeSinceText } from 'app/common/react/components/Activities/common';

const TEXT_MAX_WIDTH_PX = 160;

type OwnProps = {|
  commentActivityId: string,
  parentActivity: BoardActivityItem,
  showReplies?: boolean,
|};

type HOCProps = {|
  replyResource: Resource<Array<BoardActivityItem>>,
|};

type ReduxDispatchProps = {|
  toggleLike: (activityId: string, activityType: BoardActivityType, isLiked: boolean) => void,
|};

type ReduxStateProps = {|
  commentActivityItem: BoardActivityItem,
|};

type ConnectedProps = {|
  ...ReduxDispatchProps,
  ...ReduxStateProps,
  ...OwnProps,
|};

type Props = {|
  ...ConnectedProps,
  ...HOCProps,
|};

// Renders one board activity comment under Pin Reps in group board pin grid
export function BoardActivityPinRepComment({
  commentActivityId,
  commentActivityItem,
  parentActivity,
  replyResource,
  showReplies,
  toggleLike,
}: Props) {
  const {
    comment_count: commentCount,
    content: { text: commentText },
    created_at: commentCreatedAt,
    reaction_by_me: reactionByMe,
    type: commentActivityType,
    user: activityUser,
  } = commentActivityItem;

  const [hoverState, sethoverState] = useState(false);

  const handleOnTouch = ({ event }) => {
    toggleLike(commentActivityId, commentActivityType, !!reactionByMe);
  };

  const handleOnMouseEnter = ({ event }) => {
    sethoverState(true);
  };

  const handleOnMouseLeave = ({ event }) => {
    sethoverState(false);
  };

  const moreRepliesCount = showReplies && commentCount ? commentCount - 1 : 0;

  const showReplyPlaceholder = !replyResource.isLoaded;
  const replyActivityId =
    !!replyResource.isLoaded &&
    !!replyResource.data &&
    replyResource.data.length > 0 &&
    replyResource.data[0].id;

  const moreRepliesText = (
    <Text size="xs" weight="bold">
      {interpolateNamedTemplate(
        i18n.ngettext(
          '{{ moreRepliesCount }} more comment',
          '{{ moreRepliesCount }} more comments',
          moreRepliesCount,
          'Number of more comments indicator for board comments under pin cards',
        ),
        { moreRepliesCount },
      )}
    </Text>
  );

  return (
    <Box display="flex" direction="row" marginTop={2} marginBottom={1}>
      <Box minWidth={32} minHeight={32}>
        <Avatar src={activityUser.image_medium_url} name={activityUser.username} />
      </Box>

      <Box display="flex" direction="column" marginLeft={2} width="100%">
        <Touchable
          onTouch={handleOnTouch}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        >
          {/* Username and Time */}
          <Box display="flex" direction="row">
            <Box maxWidth={130}>
              <Text size="sm" weight="bold">
                {getNameToShow(activityUser)}
              </Text>
            </Box>

            <Box marginLeft={1}>
              <Text size="sm" color="gray">
                {getTimeSinceText(commentCreatedAt)}
              </Text>
            </Box>
          </Box>

          {/* Comment */}
          <Box display="flex" direction="row">
            {/* Text */}
            <Box width="100%" maxWidth={TEXT_MAX_WIDTH_PX}>
              <Text size="sm">{commentText}</Text>
            </Box>

            {/* Reaction Icon */}
            <Box marginTop={-2} marginRight={1} marginLeft={2} minWidth={12}>
              {!!reactionByMe && (
                <Icon
                  icon="heart"
                  accessibilityLabel={i18n._(
                    'Liked',
                    'Accessibility lable for like sent reaction icon to comments and replies under pin rep',
                  )}
                  color="red"
                  size={12}
                />
              )}
              {!reactionByMe &&
                hoverState && (
                  <Icon
                    icon="heart-outline"
                    accessibilityLabel={i18n._(
                      'Like',
                      'Accessibility lable for send like reaction icon to comments and replies under pin rep',
                    )}
                    color="darkGray"
                    size={12}
                  />
                )}
            </Box>
          </Box>
        </Touchable>

        {moreRepliesCount > 0 && (
          <Box marginTop={2} marginBottom={1}>
            {parentActivity && parentActivity.pin ? (
              <NavigateLink to={`/pin/${parentActivity.pin.id}/`}>{moreRepliesText}</NavigateLink>
            ) : (
              moreRepliesText
            )}
          </Box>
        )}

        {showReplies &&
          (showReplyPlaceholder ? (
            <Box
              paddingX={1}
              marginTop={2}
              width="100%"
              dangerouslySetInlineStyle={{ __style: { borderLeft: '2px solid #efefef' } }}
            >
              <Box color="lightGray" width="100%" height={48} />
            </Box>
          ) : (
            <BoardActivityPinRepReply
              parentActivity={commentActivityItem}
              replyActivityId={replyActivityId || null}
            />
          ))}
      </Box>
    </Box>
  );
}

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  toggleLike: (activityId: string, activityType: BoardActivityType, isLiked: boolean) =>
    dispatch(
      toggleActivityItemReaction(
        activityId,
        PCONST.thrift.ReactionType.LIKE,
        activityType,
        isLiked,
      ),
    ),
});

const mapStateToProps = (state: ReduxState, props: OwnProps): ReduxStateProps => {
  const commentActivityItem = state.activityItems[props.commentActivityId];
  return {
    commentActivityItem,
  };
};

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withResource(
    {
      name: 'BoardActivityCommentFeedResource',
      key: 'replyResource',
      options: (props) => ({
        page_size: 1,
        activityId: props.commentActivityItem && props.commentActivityItem.id,
        parentType: props.commentActivityItem && props.commentActivityItem.type,
        redux_normalize_feed: true,
      }),
    },
    { allowStale: false },
  ),
)(BoardActivityPinRepComment);
